/* eslint-disable @typescript-eslint/no-explicit-any */
// Enhanced Analytics utility with HotJar integration and funnel tracking
// Note: Using `any` types is intentional for third-party GA/HotJar integration

import { hasAnalyticsConsent } from "./consent";

declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string,
      config?: Record<string, unknown>
    ) => void;
    dataLayer?: unknown[];
    hj?: (...args: unknown[]) => void;
    _hjSettings?: { hjid: number; hjsv: number };
  }
}

// Get environment variables
const GA_MEASUREMENT_ID = import.meta.env.VITE_GOOGLE_ANALYTICS_ID;
const HOTJAR_ID = import.meta.env.VITE_HOTJAR_ID;

/**
 * Initialize Google Analytics
 */
export const initializeGA = () => {
  if (typeof window === "undefined" || !GA_MEASUREMENT_ID) return;

  // Check if gtag script is already loaded
  if (!document.querySelector('script[src*="gtag"]')) {
    // Create gtag script
    const gtagScript = document.createElement("script");
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(gtagScript);

    // Create gtag config script
    const configScript = document.createElement("script");
    configScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${GA_MEASUREMENT_ID}', {
        page_title: document.title,
        page_location: window.location.href,
        send_page_view: true
      });
    `;
    document.head.appendChild(configScript);
  }
};

/**
 * Initialize HotJar
 */
export const initializeHotJar = () => {
  if (typeof window === "undefined" || !HOTJAR_ID) return;

  try {
    if (!window.hj) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, prefer-rest-params
      (function (h: any, o: any, t: any, j: any, a: any, r: any) {
        h.hj =
          h.hj ||
          function (...args: unknown[]) {
            (h.hj.q = h.hj.q || []).push(args);
          };
        h._hjSettings = { hjid: parseInt(HOTJAR_ID), hjsv: 6 };
        a = o.getElementsByTagName("head")[0];
        r = o.createElement("script");
        r.async = 1;
        r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
        a.appendChild(r);
      })(
        window,
        document,
        "https://static.hotjar.com/c/hotjar-",
        ".js?sv=",
        undefined,
        undefined
      );
    }
  } catch (error) {
    console.error("HotJar initialization error:", error);
  }
};

/**
 * Track page views with hash routing support
 */
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  // Google Analytics
  if (typeof window !== "undefined" && window.gtag && GA_MEASUREMENT_ID) {
    window.gtag("config", GA_MEASUREMENT_ID, {
      page_path: pagePath,
      page_title: pageTitle ?? document.title,
      page_location: window.location.href,
    });
  }

  // HotJar state change
  if (typeof window !== "undefined" && window.hj) {
    window.hj("stateChange", pagePath);
  }
};

/**
 * Track custom events
 */
export const trackEvent = (
  eventName: string,
  eventParams?: {
    event_category?: string;
    event_label?: string;
    value?: number | undefined;
    source?: string;
    [key: string]: string | number | boolean | undefined;
  }
) => {
  // Google Analytics
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", eventName, {
      event_category: eventParams?.event_category ?? "engagement",
      event_label: eventParams?.event_label,
      value: eventParams?.value,
      ...eventParams,
    });
  }

  // HotJar event
  if (typeof window !== "undefined" && window.hj) {
    window.hj("event", eventName);
  }
};

/**
 * Enhanced funnel tracking for waitlist conversion
 */
export const trackFunnelStep = (
  step:
    | "landing_view"
    | "landing_engagement"
    | "waitlist_start"
    | "form_fill"
    | "form_submit"
    | "email_verification"
    | "verification_complete",
  metadata?: {
    time_on_page?: number;
    scroll_depth?: number;
    interactions?: number;
    source?: string;
    [key: string]: string | number | boolean | undefined;
  }
) => {
  trackEvent("funnel_step", {
    event_category: "funnel",
    event_label: step,
    funnel_step: step,
    ...metadata,
  });

  // HotJar funnel tracking
  if (typeof window !== "undefined" && window.hj) {
    window.hj("event", `funnel_${step}`);
  }
};

/**
 * Track user engagement metrics
 */
export const trackEngagement = (
  action: "scroll" | "click" | "hover" | "form_focus" | "form_blur",
  element: string,
  metadata?: {
    page?: string;
    section?: string;
    time_on_page?: number;
    [key: string]: string | number | boolean | undefined;
  }
) => {
  trackEvent("user_engagement", {
    event_category: "engagement",
    event_label: `${action}_${element}`,
    engagement_action: action,
    element: element,
    ...metadata,
  });
};

/**
 * Track form analytics
 */
export const trackFormAnalytics = (
  action:
    | "focus"
    | "blur"
    | "input"
    | "validation_error"
    | "submit_attempt"
    | "submit_success",
  formName: string,
  fieldName?: string,
  metadata?: {
    time_to_complete?: number;
    errors_count?: number;
    [key: string]: string | number | boolean | undefined;
  }
) => {
  trackEvent("form_interaction", {
    event_category: "forms",
    event_label: `${action}_${formName}`,
    form_name: formName,
    field_name: fieldName,
    form_action: action,
    ...metadata,
  });
};

/**
 * Track conversion events with enhanced metadata
 */
export const trackConversion = (
  conversionType:
    | "waitlist_signup"
    | "email_verified"
    | "demo_start"
    | "landing_engagement",
  source?: string,
  value?: number | undefined,
  metadata?: {
    user_type?: string;
    referral_source?: string;
    device_type?: string;
    [key: string]: string | number | boolean | undefined;
  }
) => {
  trackEvent("conversion", {
    event_category: "conversions",
    event_label: conversionType,
    conversion_type: conversionType,
    source: source ?? "unknown",
    value: value,
    ...metadata,
  });

  // HotJar conversion tracking
  if (typeof window !== "undefined" && window.hj) {
    window.hj("event", `conversion_${conversionType}`);
  }
};

/**
 * Track user journey for communication app
 */
export const trackUserJourney = (
  action: "page_view" | "section_view" | "feature_use" | "drop_off",
  details: {
    page?: string;
    section?: string;
    feature?: string;
    time_spent?: number;
    [key: string]: string | number | boolean | undefined;
  }
) => {
  trackEvent("user_journey", {
    event_category: "user_journey",
    event_label: action,
    journey_action: action,
    ...details,
  });
};

/**
 * Track returning users
 */
export const trackReturnUser = (
  isReturning: boolean,
  metadata?: {
    days_since_last_visit?: number;
    total_visits?: number;
    first_visit_date?: string;
    last_visit_date?: string;
    [key: string]: string | number | boolean | undefined;
  }
) => {
  trackEvent("user_return", {
    event_category: "user_behavior",
    event_label: isReturning ? "returning_user" : "new_user",
    is_returning_user: isReturning,
    ...metadata,
  });

  // Set user properties for segmentation
  if (typeof window !== "undefined" && window.gtag && GA_MEASUREMENT_ID) {
    window.gtag("config", GA_MEASUREMENT_ID, {
      custom_map: {
        user_type: isReturning ? "returning" : "new",
        total_visits: metadata?.total_visits ?? 1,
        first_visit: metadata?.first_visit_date,
      },
    });
  }

  // HotJar user properties
  if (typeof window !== "undefined" && window.hj) {
    window.hj("identify", undefined, {
      user_type: isReturning ? "returning" : "new",
      total_visits: metadata?.total_visits ?? 1,
      first_visit: metadata?.first_visit_date,
    });
  }
};

/**
 * Check if user is returning and track accordingly
 */
export const checkAndTrackReturnUser = () => {
  if (typeof window === "undefined") return;

  const now = new Date();
  const storageKey = "koru_user_visits";

  try {
    // Get existing visit data
    const existingData = localStorage.getItem(storageKey);
    let visitData: {
      firstVisit: string;
      lastVisit: string;
      totalVisits: number;
      visits: string[];
    };

    if (existingData) {
      visitData = JSON.parse(existingData);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const daysSinceLastVisit = Math.floor(
        (now.getTime() - new Date(visitData.lastVisit).getTime()) /
          (1000 * 60 * 60 * 24)
      );

      // Update visit data
      visitData.lastVisit = now.toISOString();
      visitData.totalVisits += 1;
      visitData.visits.push(now.toISOString());

      // Track return user
      trackReturnUser(true, {
        days_since_last_visit: daysSinceLastVisit,
        total_visits: visitData.totalVisits,
        first_visit_date: visitData.firstVisit,
        last_visit_date: visitData.lastVisit,
      });

      // Clean up old visits (keep last 10)
      if (visitData.visits.length > 10) {
        visitData.visits = visitData.visits.slice(-10);
      }
    } else {
      // First time user
      visitData = {
        firstVisit: now.toISOString(),
        lastVisit: now.toISOString(),
        totalVisits: 1,
        visits: [now.toISOString()],
      };

      trackReturnUser(false, {
        total_visits: 1,
        first_visit_date: visitData.firstVisit,
        last_visit_date: visitData.lastVisit,
      });
    }

    // Store updated data
    localStorage.setItem(storageKey, JSON.stringify(visitData));
  } catch (error) {
    console.warn("Error tracking return user:", error);
  }
};

/**
 * Get user visit statistics
 */
export const getUserVisitStats = () => {
  if (typeof window === "undefined") return null;

  try {
    const storageKey = "koru_user_visits";
    const existingData = localStorage.getItem(storageKey);

    if (existingData) {
      const visitData = JSON.parse(existingData);
      const now = new Date();
      const daysSinceLastVisit = Math.floor(
        (now.getTime() - new Date(visitData.lastVisit).getTime()) /
          (1000 * 60 * 60 * 24)
      );

      return {
        isReturning: visitData.totalVisits > 1,
        totalVisits: visitData.totalVisits,
        firstVisit: visitData.firstVisit,
        lastVisit: visitData.lastVisit,
        daysSinceLastVisit,
      };
    }

    return null;
  } catch (error) {
    console.warn("Error getting user visit stats:", error);
    return null;
  }
};

/**
 * Track performance metrics
 */
export const trackPerformance = (
  metric: "page_load" | "interaction_delay" | "form_submission_time",
  value: number,
  metadata?: {
    page?: string;
    element?: string;
    [key: string]: any;
  }
) => {
  trackEvent("performance", {
    event_category: "performance",
    event_label: metric,
    performance_metric: metric,
    value: Math.round(value),
    ...metadata,
  });
};

/**
 * Track errors and exceptions
 */
export const trackError = (
  errorName: string,
  errorDescription?: string,
  fatal?: boolean,
  metadata?: {
    page?: string;
    user_action?: string;
    [key: string]: string | number | boolean | undefined;
  }
) => {
  // Google Analytics
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "exception", {
      description: `${errorName}: ${errorDescription}`,
      fatal: fatal ?? false,
      ...metadata,
    });
  }

  // HotJar error tracking
  if (typeof window !== "undefined" && window.hj) {
    window.hj("event", "error_occurred");
  }
};

/**
 * Set user properties for segmentation
 */
export const setUserProperties = (properties: {
  user_id?: string;
  user_type?: "waitlist" | "verified" | "beta_user";
  signup_method?: string;
  referral_source?: string;
  [key: string]: any;
}) => {
  if (typeof window !== "undefined" && window.gtag && GA_MEASUREMENT_ID) {
    window.gtag("config", GA_MEASUREMENT_ID, {
      user_id: properties.user_id,
      custom_map: properties,
    });
  }

  // HotJar user properties
  if (typeof window !== "undefined" && window.hj) {
    window.hj("identify", properties.user_id, properties);
  }
};

/**
 * Track timing events for funnel analysis
 */
export const trackTiming = (
  name: string,
  value: number,
  category?: string,
  label?: string
) => {
  trackEvent("timing_complete", {
    event_category: category ?? "user_timing",
    event_label: label ?? name,
    name: name,
    value: Math.round(value),
  });
};

/**
 * Initialize all analytics
 * NOTE: This should only be called AFTER consent is granted.
 * For initial page load, use initializeAnalyticsWithConsent instead.
 */
export const initializeAnalytics = () => {
  initializeGA();
  initializeHotJar();

  // Track initial page load
  trackPageView(
    window.location.pathname + window.location.hash,
    document.title
  );

  // Track performance metrics
  if ("performance" in window) {
    window.addEventListener("load", () => {
      const perfData = performance.getEntriesByType(
        "navigation"
      )[0] as PerformanceNavigationTiming;
      if (perfData) {
        trackPerformance(
          "page_load",
          perfData.loadEventEnd - perfData.loadEventStart
        );
      }
    });
  }
};

/**
 * Initialize analytics only if user has given consent.
 * This should be called on app initialization.
 * If no consent, analytics will NOT be loaded - waiting for CookieConsent banner action.
 */
export const initializeAnalyticsWithConsent = () => {
  if (hasAnalyticsConsent()) {
    initializeAnalytics();
    return true;
  }
  // No consent yet - analytics will be initialized after consent is granted
  return false;
};

/**
 * Track tier selection for Paid-to-Free ratio (PMF metric)
 */
export const trackTierSelection = (
  tier: "free" | "priority" | "founding",
  metadata?: {
    price?: number;
    source?: string;
    previous_tier?: string;
  }
) => {
  trackEvent("tier_selection", {
    event_category: "conversion",
    event_label: tier,
    tier: tier,
    price:
      metadata?.price ??
      (tier === "priority" ? 1 : tier === "founding" ? 5 : 0),
    ...metadata,
  });

  // HotJar tier tracking
  if (typeof window !== "undefined" && window.hj) {
    window.hj("event", `tier_selected_${tier}`);
  }
};

/**
 * Track upsell funnel steps
 */
export const trackUpsellFunnel = (
  step:
    | "offer_shown"
    | "tier_viewed"
    | "tier_selected"
    | "payment_initiated"
    | "payment_complete"
    | "payment_cancelled",
  metadata?: {
    tier?: string;
    value?: number;
    source?: string;
  }
) => {
  trackEvent("upsell_funnel", {
    event_category: "funnel",
    event_label: step,
    funnel_step: step,
    ...metadata,
  });

  // HotJar funnel tracking
  if (typeof window !== "undefined" && window.hj) {
    window.hj("event", `upsell_${step}`);
  }
};

/**
 * Track payment flow events (for future Stripe/Link integration)
 */
export const trackPaymentFlow = (
  action: "initiated" | "processing" | "success" | "failed" | "cancelled",
  metadata?: {
    tier?: string;
    amount?: number;
    payment_method?: string;
    error_message?: string;
  }
) => {
  trackEvent("payment_flow", {
    event_category: "payment",
    event_label: action,
    payment_action: action,
    ...metadata,
  });

  // Track as conversion if successful
  if (action === "success" && metadata?.tier) {
    trackConversion("waitlist_signup", "payment", metadata.amount, {
      tier: metadata.tier,
      payment_method: metadata.payment_method,
    });
  }
};

// Export all functions
export default {
  initializeAnalytics,
  initializeAnalyticsWithConsent,
  initializeGA,
  initializeHotJar,
  trackPageView,
  trackEvent,
  trackFunnelStep,
  trackEngagement,
  trackFormAnalytics,
  trackConversion,
  trackUserJourney,
  trackReturnUser,
  checkAndTrackReturnUser,
  getUserVisitStats,
  trackPerformance,
  trackError,
  setUserProperties,
  trackTiming,
  trackTierSelection,
  trackUpsellFunnel,
  trackPaymentFlow,
};
